/**
 * CC-037 — jetton-wallet address derivation.
 *
 * To sell, the user sends a TEP-74 transfer to THEIR OWN jetton wallet
 * for the token's master. That wallet address is deterministic but not
 * something we can hardcode — it depends on (master, owner). We derive
 * it on-chain via the TEP-89 `get_wallet_address` get-method, exactly
 * as the bot's dexMigration code does server-side.
 *
 * The RPC endpoint is a public toncenter node picked from
 * VITE_TON_NETWORK; VITE_TON_RPC overrides it (e.g. an API-keyed node).
 */

import { Address, beginCell } from '@ton/core';
import { TonClient } from '@ton/ton';

function rpcEndpoint(): string {
  const override = import.meta.env.VITE_TON_RPC;
  if (override && override.length > 0) return override;
  return import.meta.env.VITE_TON_NETWORK === 'mainnet'
    ? 'https://toncenter.com/api/v2/jsonRPC'
    : 'https://testnet.toncenter.com/api/v2/jsonRPC';
}

let _client: TonClient | null = null;
function tonClient(): TonClient {
  if (!_client) _client = new TonClient({ endpoint: rpcEndpoint() });
  return _client;
}

/**
 * Derive `owner`'s jetton wallet for `jettonMaster` by calling the
 * master's TEP-89 `get_wallet_address` get-method. NOT hardcoded — each
 * (master, owner) pair maps to a unique deterministic wallet address.
 *
 * Callers should cache the result per (master, owner) — it never
 * changes — rather than re-running the get-method on every render.
 */
export async function getJettonWalletAddress(
  jettonMaster: Address,
  owner: Address,
): Promise<Address> {
  const res = await tonClient().runMethod(jettonMaster, 'get_wallet_address', [
    { type: 'slice', cell: beginCell().storeAddress(owner).endCell() },
  ]);
  return res.stack.readAddress();
}
